import classNames from 'classnames';
import type { Stage as KonvaStage } from 'konva/lib/Stage';
import React, { useMemo, useRef, useState, useCallback } from 'react';

import { KonvaImagePromisesProvider } from 'common/components/RewindModal/Konvas/KonvaImagePromisesProvider';
import ShareableScreenRepartition from 'common/components/RewindModal/Konvas/ShareableScreenRepartition';
import Slides from 'common/components/RewindModal/Slides';
import {
  MEMBER_CARD_MOVIE,
  MEMBER_CARD_SERIES,
  MEMBER_CARD_SERIES_MOVIE
} from 'common/configuration/constants';

import styles from '../styles.module.scss';
import BottomActions from './component/BottomActions';
import StatsGauge from './component/StatsGauge';

type ScreenRepartitionProps = {
  data: {
    movie_count: number;
    series_count: number;
    phrase_1?: string;
    phrase_2?: string;
  };
  userNickname?: string;
  allScreens?: any[];
  onNext: () => void;
  isLastStep: boolean;
  trackingScreenName?: string;
};

const ScreenRepartitionContent = ({
  data,
  userNickname,
  allScreens,
  onNext,
  isLastStep,
  isDownloadReady
}: ScreenRepartitionProps & { isDownloadReady: boolean }) => {
  const stageRef = useRef<KonvaStage>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  const flashbackType = useMemo(() => {
    if (!allScreens) return undefined;
    const flashbackScreen = allScreens.find(s => s.name === 'flashback_type');
    return flashbackScreen?.data?.flashback_type;
  }, [allScreens]);

  const isType6 = flashbackType === 6;

  const movieCount = data?.movie_count ?? 0;
  const seriesCount = data?.series_count ?? 0;

  const memberType = useMemo(
    () =>
      !isType6
        ? movieCount > seriesCount
          ? 'movie'
          : seriesCount > movieCount
            ? 'series'
            : 'seriemovie'
        : null,
    [movieCount, seriesCount, isType6]
  );

  const globalClassName = useMemo(() => {
    return isType6 ? styles.centeredLayout : '';
  }, [isType6]);

  const memberCardImage = useMemo(
    () =>
      !isType6 && memberType
        ? memberType === 'movie'
          ? MEMBER_CARD_MOVIE
          : memberType === 'series'
            ? MEMBER_CARD_SERIES
            : MEMBER_CARD_SERIES_MOVIE
        : null,
    [memberType, isType6]
  );

  const moviesBarClassname = useMemo(
    () =>
      movieCount === seriesCount
        ? 'v-50'
        : movieCount > seriesCount
          ? `v-100`
          : (() => {
              let percent = Math.round(
                (movieCount / (movieCount + seriesCount)) * 100
              );
              if (percent < 15) percent += 10;
              return `v-${percent}`;
            })(),
    [movieCount, seriesCount]
  );

  const seriesBarClassname = useMemo(
    () =>
      seriesCount === movieCount
        ? 'v-50'
        : seriesCount > movieCount
          ? 'v-100'
          : (() => {
              let percent = Math.round(
                (seriesCount / (movieCount + seriesCount)) * 100
              );
              if (percent < 15) percent += 10;
              return `v-${percent}`;
            })(),
    [movieCount, seriesCount]
  );

  const handleDownload = useCallback(async () => {
    if (!isDownloadReady || isDownloading || !stageRef.current) {
      return;
    }

    setIsDownloading(true);

    setTimeout(() => {
      try {
        const stage = stageRef.current;
        if (!stage) {
          throw new Error('Konva stage is not available.');
        }

        const dataURL = stage.toDataURL({ pixelRatio: 2 });

        const link = document.createElement('a');
        link.download = 'ma-carte-de-membre-allocine-2025.png';
        link.href = dataURL;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        // Error handling for image generation
      } finally {
        setIsDownloading(false);
      }
    }, 100);
  }, [isDownloadReady, isDownloading]);

  return (
    <>
      <Slides>
        <div className={classNames(styles.contentLayout, globalClassName)}>
          {isType6 ? (
            <>
              {data?.phrase_1 && (
                <div className={styles.slideTitle}>{data.phrase_1}</div>
              )}
              {data?.phrase_2 && (
                <div className={styles.slideText}>{data.phrase_2}</div>
              )}
            </>
          ) : (
            <>
              <div className={styles.slideText}>
                Vous avez mérité votre carte de membre
              </div>

              {memberCardImage && (
                <div className={styles.memberCardImageWrapper}>
                  <div className={styles.memberCardNickname}>
                    {userNickname ?? 'Passionné(e)'}
                  </div>
                  <img
                    src={memberCardImage}
                    alt="Member Card"
                    className={styles.memberCardImage}
                  />
                </div>
              )}
            </>
          )}

          <div className={styles.slideText}>La preuve en chiffres :</div>
          <StatsGauge
            moviesCount={movieCount}
            seriesCount={seriesCount}
            moviesBarClassname={moviesBarClassname}
            seriesBarClassname={seriesBarClassname}
          />
        </div>
        <BottomActions
          showNext={!isLastStep}
          shareText={isDownloading ? 'Génération...' : "Partager cette stat'"}
          onShareClick={handleDownload}
          isShareDisabled={!isDownloadReady || isDownloading}
          onNextClick={onNext}
          showShare={true}
        />
      </Slides>

      <ShareableScreenRepartition
        ref={stageRef}
        data={data}
        userNickname={userNickname}
        flashbackType={flashbackType}
      />
    </>
  );
};

const ScreenRepartition = (props: ScreenRepartitionProps) => {
  const [isDownloadReady, setIsDownloadReady] = useState(false);

  return (
    <KonvaImagePromisesProvider onDownloadableChange={setIsDownloadReady}>
      <ScreenRepartitionContent {...props} isDownloadReady={isDownloadReady} />
    </KonvaImagePromisesProvider>
  );
};

export default ScreenRepartition;
